import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Star,
  Edit,
  Eye,
  MoreHorizontal,
  Trash,
  Rocket,
  TrendingUp,
  Building,
  MessageCircle,
} from "lucide-react";

interface ListingType {
  id: string;
  title: string;
  address: string;
  price: string;
  type: string;
  status: string;
  image: string;
  featured: boolean;
  boosted: boolean;
}

interface ListingGridViewProps {
  listings: ListingType[];
  onOpenFeaturedDialog: (id: string) => void;
  onOpenBoostDialog: (id: string) => void;
}

const ListingGridView = ({
  listings,
  onOpenFeaturedDialog,
  onOpenBoostDialog,
}: ListingGridViewProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {listings.map((property) => (
        <Card key={property.id} className="hover:shadow-md transition-all">
          <CardHeader className="pb-2 space-y-0">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg font-medium">
                  {property.title}
                </CardTitle>
                <CardDescription className="mt-1">
                  {property.address}
                </CardDescription>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Eye className="mr-2 h-4 w-4" />
                    <span>View Details</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Edit className="mr-2 h-4 w-4" />
                    <span>Edit Listing</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onOpenFeaturedDialog(property.id)}
                  >
                    <Star className="mr-2 h-4 w-4" />
                    <span>Mark as Featured</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onOpenBoostDialog(property.id)}
                  >
                    <Rocket className="mr-2 h-4 w-4" />
                    <span>Boost Listing</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600">
                    <Trash className="mr-2 h-4 w-4" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative aspect-video w-full overflow-hidden rounded-md mb-4">
              <img
                src={property.image}
                alt={property.title}
                className="h-full w-full object-cover"
              />
              {property.featured && (
                <div className="absolute top-3 left-3 bg-amber-500 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center text-white gap-1 shadow-md">
                  <Star size={10} className="fill-white text-white" />
                  Featured
                </div>
              )}
              {property.boosted && (
                <div className="absolute top-3 right-3 bg-blue-500 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center text-white gap-1 shadow-md">
                  <Rocket size={10} className="text-white" />
                  Boosted
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              <div className="font-medium text-lg">{property.price}</div>
              <div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    property.status === "Active"
                      ? "bg-green-100 text-green-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {property.status}
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" size="sm">
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button size="sm">
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default ListingGridView;
